import { REPORT_CONFIGURATION_NAME } from "../types/constants";
import { isReportNameInUse } from "../utils/reportDialogUtils";
import { useImportReport } from "./useImportReport";
import { useCallback } from "react";
import {
  useLazyGetReportConfigurationQuery,
  useLazyGetReportQuery,
} from "services/reportsV3Service";

// Fields that belong to the saved report rather than to its definition: the
// copy gets its own id, owner and dates, and starts private and editable.
const OWN_FIELDS = [
  "internalName",
  "configuration",
  "readOnly",
  "allUsers",
  "canWrite",
  "canWriteMetadata",
  "userId",
  "created",
  "updated",
];

/** Returns a saved report's definition without the fields of the original. */
export const duplicateReportData = (report) =>
  Object.fromEntries(
    Object.entries(report ?? {}).filter(([key]) => !OWN_FIELDS.includes(key))
  );

/** Returns "<name> (copy)", numbered when that name is already taken. */
export const duplicateReportName = (name, savedReports) => {
  const base = `${(name ?? "").trim()} (copy)`;
  let candidate = base;
  for (let n = 2; isReportNameInUse(candidate, savedReports, null); n++) {
    candidate = `${(name ?? "").trim()} (copy ${n})`;
  }
  return candidate;
};

/**
 * Copies a saved report: loads its definition and layout, then asks for the
 * copy's name in the same dialog as an import.
 */
export const useDuplicateReport = (savedReports) => {
  const dialog = useImportReport(
    savedReports,
    "Couldn't copy the report. Try again."
  );
  const { openWithData } = dialog;
  const [fetchReport] = useLazyGetReportQuery();
  const [fetchConfiguration] = useLazyGetReportConfigurationQuery();

  const handleDuplicate = useCallback(
    async (reportId) => {
      const original = savedReports?.find((r) => r.internalName === reportId);
      const name = duplicateReportName(
        original?.displayName ?? "",
        savedReports
      );
      const result = await fetchReport(reportId);
      if (!result.data) {
        openWithData(
          null,
          name,
          "Couldn't load this report to copy it. Try again."
        );
        return;
      }
      const configResult = await fetchConfiguration({
        reportId,
        configurationName: REPORT_CONFIGURATION_NAME,
      });
      const configuration = configResult.data ?? {};
      openWithData(
        {
          report: duplicateReportData(result.data),
          ...(Object.keys(configuration).length > 0 ? { configuration } : {}),
          sourceReportId: reportId,
        },
        name
      );
    },
    [savedReports, fetchReport, fetchConfiguration, openWithData]
  );

  return { ...dialog, handleDuplicate };
};
